// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import { Component, OnInit } from '@angular/core';
import { HeaderService } from 'src/app/header.service';
import { MatTableDataSource } from '@angular/material/table';
import { UntypedFormControl } from '@angular/forms';
import {BehaviorSubject, of} from 'rxjs';
import { catchError } from 'rxjs/operators';
import { EventsService } from 'src/services/events-service';
import { SelectedOrgMethod } from 'src/shared/selectedOrg';
import multiDownload from 'multi-download';
import { D3eService } from 'src/services/d3e-service';
import { IThreatCsvToken } from 'src/types/threat';
import { IColumnChooserElement } from 'src/app/shared/components/column-chooser/column-chooser.component';
import {ConfigurationService} from '../../../services/configuration-service';
import { TranslateService } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

interface Time {
  value: string;
  displayValue: string;
}

interface FilterSelectionType {
  value: string;
  displayValue: string;
}

interface SeverityType {
  value: number;
  displayValue: string;
}

interface EventData {
  id: number;
  groupName: string | null;
  timestamp: number | null;
  timestampStr: string | null;
  severity: string;
  type: string;
  description: string;
  detail: string | null;
  source: string | null;
  integrationName: string | null;
  data: string | null;
  username: string | null;
}

const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;
const WEEK = DAY * 7;

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-event-viewer',
    templateUrl: './event-viewer.component.html',
    styleUrls: ['./event-viewer.component.scss'],
    standalone: false
})
export class EventViewerComponent implements OnInit {
  dataLoaded$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  selectedOrgId: string;

  columns: IColumnChooserElement[] = [];

  columnsToDisplay: string[] = [];

  searchEnabled = true;
  columnsForm = new UntypedFormControl(this.columnsToDisplay);
  sourcesForm = new UntypedFormControl();
  severityForm = new UntypedFormControl();
  eventTypeForm = new UntypedFormControl();

  selectedTime = '1';
  searchingEvents = false;

  queryPageStart = 0;
  queryPageSize = 50;
  queryTimeStart = new Date();

  totalResults = null;
  percentLoaded = '0';
  searchValue = null;

  timestampStart = new Date().getTime();
  timestampEnd = this.timestampStart - (HOUR * 1);
  selectedSources: string[] = [];
  selectedSeverity: SeverityType;
  selectedEventTypes: string[] = [];

  eventTypes: string[] = [];

  sInternal: string;
  sConsole: string;
  sD3E: string;
  sIntegration: string;
  sourceTypes: FilterSelectionType[];
  severities: SeverityType[];

  tHour: string;
  tHours: string;
  tDay: string;
  tWeek: string;
  tWeeks: string;
  tAll: string;
  times: Time[];

  sortAttrs = [{ 'sortAttr': 'timestamp', 'sortOrder': 'desc' }];

  eventsData: EventData[] = [];

  eventDataSource: MatTableDataSource<EventData> = new MatTableDataSource<EventData>();

  urlTime = null;

  hasUserBeenSet = false;
  delayedRefreshRequested = false;

  // EVENT VIEWER FIELDS //
  fPlaceholder: string;
  fTimeFilter: string;
  fExportCSV: string;
  fExportJSON: string;
  fSourceFilter: string;
  fSeverityFilter: string;
  fEventFilter: string;
  fEventNumber: string;
  fTimestamp: string;
  fGroup: string;
  fUser: string;
  fEUser: string;
  fIntegration: string;
  fSeverity: string;
  fEvent: string;
  fSource: string;
  fDescription: string;
  fDetail: string;
  fData: string;
  fNone: string;

  title: string;
  constructor(
    private headerService: HeaderService,
    private eventsService: EventsService,
    private d3eService: D3eService,
    private configurationService: ConfigurationService,
    private translate: TranslateService
  ) {
    headerService.setContextHelp(['help.event-viewer']);
    this.translate.get(['EVI_PLACEHOLDER', 'EVI_TIME_FILTER', 'EVI_EXPORT_CSV', 'EVI_EXPORT_JSON', 'EVI_SOURCE_FILTER',
      'EVI_SEVERITY_FILTER', 'EVI_EVENT_FILTER', 'EVI_EVENT_NUMBER', 'EVI_TIMESTAMP', 'EVI_GROUP', 'EVI_USER',
      'EVI_EUSER', 'EVI_INT', 'EVI_SEVERITY', 'EVI_EVENT', 'EVI_SOURCE', 'EVI_DESC', 'EVI_DETAIL', 'EVI_DATA',
      'EVI_NONE', 'TITLE_EVENT_VIEWER', 'EVI_INTERNAL', 'EVI_CONSOLE', 'EVI_D3E', 'EVI_INTEGRATION', 'EVI_HOUR',
      'EVI_HOURS', 'EVI_DAY', 'EVI_WEEK', 'EVI_WEEKS', 'EVI_ALL',
      'SEVERITY_DEBUG', 'SEVERITY_INFO', 'SEVERITY_WARNING', 'SELECTOR_ALL_EVENTS']).subscribe(vals => {
      this.fPlaceholder = vals['EVI_PLACEHOLDER'];
      this.fTimeFilter = vals['EVI_TIME_FILTER'];
      this.fExportCSV = vals['EVI_EXPORT_CSV'];
      this.fExportJSON = vals['EVI_EXPORT_JSON'];
      this.fSourceFilter = vals['EVI_SOURCE_FILTER'];
      this.fSeverityFilter = vals['EVI_SEVERITY_FILTER'];
      this.fEventFilter = vals['EVI_EVENT_FILTER'];
      this.fEventNumber = vals['EVI_EVENT_NUMBER'];
      this.fTimestamp = vals['EVI_TIMESTAMP'];
      this.fGroup = vals['EVI_GROUP'];
      this.fUser = vals['EVI_USER'];
      this.fEUser = vals['EVI_EUSER'];
      this.fIntegration = vals['EVI_INT'];
      this.fSeverity = vals['EVI_SEVERITY'];
      this.fEvent = vals['EVI_EVENT'];
      this.fSource = vals['EVI_SOURCE'];
      this.fDescription = vals['EVI_DESC'];
      this.fDetail = vals['EVI_DETAIL'];
      this.fData = vals['EVI_DATA'];
      this.fNone = vals['EVI_NONE'];

      this.title = vals['TITLE_EVENT_VIEWER'];

      this.severities = [ { 'value': 40, 'displayValue': translate.instant('SEVERITY_ERROR') },
                          { 'value': 30, 'displayValue': translate.instant('SEVERITY_WARNING') },
                          { 'value': 20, 'displayValue': translate.instant('SEVERITY_INFO') },
                          { 'value': 10, 'displayValue': translate.instant('SEVERITY_DEBUG') },
                        ];

      this.selectedSeverity = this.severities[2];
      this.severityForm.setValue(this.selectedSeverity);

      this.columns = [
        { title: this.fTimestamp, value: 'timestamp', selected: true, hidden: false, },
        { title: this.fGroup, value: 'groupName', selected: false, hidden: false, },
        { title: this.fUser, value: 'username', selected: true, hidden: false, },
        { title: this.fIntegration, value: 'integrationName', selected: false, hidden: false, },
        { title: this.fSeverity, value: 'severity', selected: true, hidden: false, },
        { title: this.fEvent, value: 'type', selected: true, hidden: false, },
        { title: this.fSource, value: 'source', selected: true, hidden: false, },
        { title: this.fDescription, value: 'description', selected: true, hidden: false, },
        { title: this.fDetail, value: 'detail', selected: false, hidden: false, },
        { title: this.fData, value: 'data', selected: false, hidden: false, },
      ];
      this.sInternal = vals['EVI_INTERNAL'];
      this.sConsole = vals['EVI_CONSOLE'];
      this.sD3E = vals['EVI_D3E'];
      this.sIntegration = vals['EVI_INTEGRATION'];
      this.sourceTypes = [
        { value: '1', displayValue: this.sConsole },
        { value: '4', displayValue: 'Agent' },
        { value: '5', displayValue: 'PBA' },
        { value: '6', displayValue: 'FDE' },
        { value: '2', displayValue: 'Data Defense' },
        { value: '0', displayValue: this.sInternal },
        { value: '3', displayValue: this.sIntegration },
      ];
      this.tHour = vals['EVI_HOUR'];
      this.tHours = vals['EVI_HOURS'];
      this.tDay = vals['EVI_DAY'];
      this.tWeek = vals['EVI_WEEK'];
      this.tWeeks = vals['EVI_WEEKS'];
      this.tAll = vals['EVI_ALL'];
      this.times = [
        { value: '1', displayValue: '1 ' + this.tHour },
        { value: '3', displayValue: '3 ' + this.tHour },
        { value: '24', displayValue: ' 1 ' + this.tDay },
        { value: '168', displayValue: '1 ' + this.tWeek },
        { value: '336', displayValue: '2 ' + this.tWeeks },
        { value: '672', displayValue: '4 ' + this.tWeeks },
        { value: '2016', displayValue: '12 ' + this.tWeeks },
        { value: 'All', displayValue: this.tAll }
      ];

      this.headerService.setOrgSelectorDefaultOption(vals['SELECTOR_ALL_EVENTS']);

    });
    const storageTime = localStorage.getItem('timeSelection');
    if (storageTime !== undefined && storageTime != null) {
      const hourStr = storageTime.split('h')[0];
      this.setTimeInterval(hourStr);
    } 
    const eventCols = localStorage.getItem('eventCols');
    if (eventCols !== undefined && eventCols != null) {
      this.columnsToDisplay = JSON.parse(eventCols);
      this.columnsForm.setValue(this.columnsToDisplay, { onlySelf: true });
    }

    this.headerService.setTitle(this.title);
    this.headerService.setSubtitle('');
    this.headerService.setIcon('mdi-calendar');
    this.headerService.setNavMenuText('');
    this.headerService.showUserFilter(true);
    this.headerService.showRefreshButton(true);
    this.headerService.refresh$.pipe(untilDestroyed(this)).subscribe(refresh => {
      if (refresh) {
        this.refreshEventsViewer();
      }
    })

    this.headerService.orgSelectedChanged
      .pipe(untilDestroyed(this))
      .subscribe(value => {
        this.selectedOrgId = value;
        if ( this.hasUserBeenSet ) {
          this.refreshEventsViewer();
        }
        this.hasUserBeenSet = true;
    });

  }

  private setTimeInterval(hourStr: any, updateSelectedTime = true): void {
    if (hourStr === 'All') {
      this.timestampEnd = null;
      if (updateSelectedTime) {
        this.selectedTime = hourStr;
      }
    } else {
      const hours = parseInt(hourStr, 10);
      this.timestampEnd = this.timestampStart - (HOUR * hours);
      if (updateSelectedTime) {
        this.selectedTime = hourStr;
      }
    }
  }

  ngOnInit() {
    this.eventDataSource.data = [];
    this.totalResults = null;
    this.percentLoaded = '25';
    this.getEventColumns();
  }

  onSearchKeyUp(searchTerm) {
    this.searchValue = searchTerm;
  }

  refreshEventsViewer() {
    // if a request is currently in process, the data can come back before the request is started.  This puts old data
    // at the top of the list and the new "refresh" data is on the second page.  Here we set delayedRefreshRequested
    // instead of doing a get so that when the existing query returns the refresh will be done in the get's
    // complete() block
    this.delayedRefreshRequested = false;
    if (! this.searchingEvents) {
      this.timestampStart = new Date().getTime();
      this.setTimeInterval(this.selectedTime, false);
      this.queryPageStart = 0;
      this.eventsData = [];
      this.searchEvents();
    } else {
      // a query is in process right now, mark that when query returns we want to do a refresh
      this.delayedRefreshRequested = true;
    }
  }

  getEventColumns() {
    this.eventsService.getEventColumns()
      .pipe(
          catchError(() => of([])),
          untilDestroyed(this))
      .subscribe(
        eventResponse => {
          this.eventTypes = eventResponse['types'].sort();
          this.refreshEventsViewer();
        },
        error => {
          // TODO: Do something better with the error, like notify the user.
          console.log(`Error retrieving event columns: ${error}`);
        }
      );
  }

  buildQuery() {
    const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
    return {
      'pageStart': this.queryPageStart,
      'pageSize': this.queryPageSize,
      'timeHigh': (this.timestampStart / 1000),
      'timeLow': this.timestampEnd != null ? (this.timestampEnd / 1000) : null,
      'sources': this.selectedSources,
      'severity': ( this.selectedSeverity != null ? this.selectedSeverity.value : 0 ),
      'event_types': this.selectedEventTypes,
      'sorts': this.sortAttrs,
      'search': this.searchValue,
      'selectedOrgId': selectedOrg,
      'columnsToDisplay': null,
      'translations': null
    };
  }

  searchEvents() {
    this.searchingEvents = true;
    const query = this.buildQuery();
    this.eventsService.searchEvents(query)
      .pipe(
          catchError(() => of([])),
          untilDestroyed(this))
      .subscribe(
        eventResponse => {
          if (! this.delayedRefreshRequested) {
            // a refresh is requested, don't process this response, throw it away.  the complete() below will
            // perform a refresh
            const eventsList = eventResponse['events'];
            if (eventResponse['totalCount'] != null) {
              this.totalResults = eventResponse['totalCount'];
            }
            const eventList = [];

            eventsList.forEach(event => {
              let sourceDisplay = '';
              this.sourceTypes.forEach(sourceType => {
                if (parseInt(sourceType.value, 10) === event.source) {
                  sourceDisplay = sourceType.displayValue;
                }
              });
              let severityDisplay = event.severity;
              this.severities.forEach(severity => {
                if (severity.value === event.severity) {
                  severityDisplay = severity.displayValue;
                }
              });


              event.sourceDisplay = sourceDisplay;
              event.timestamp = event.timestamp * 1000;
              event.severityDisplay = severityDisplay;
              eventList.push(event);
            });

            // else we are asking for a refresh so refresh the data.
            this.eventsData = [...this.eventsData, ...eventList];
            if (this.queryPageStart === 0) {
              this.eventDataSource.data = eventList;
            } else {
              Array.prototype.push.apply(this.eventDataSource.data, eventList);
            }
            this.dataLoaded$.next(true);
          }
        },
        error => {
          // TODO: Do something better with the error, like notify the user.
          console.log(`Error retrieving events: ${error}`);
        },
        () => {
          this.searchingEvents = false;
          if ( this.delayedRefreshRequested ) {
            this.refreshEventsViewer();
          }
        }
      );

  }

  onTimeChange(event) {
    const newValue = event.value;
    if (newValue === 'All') {
      localStorage.removeItem('timeSelection');
    } else {
      localStorage.setItem('timeSelection', newValue + 'h');
    }
    this.setTimeInterval(newValue, true);
    this.refreshEventsViewer();
  }

  onSourceChange(event) {
    this.selectedSources = [];
    if (this.sourcesForm.value !== undefined && this.sourcesForm.value.length > 0) {
      this.sourcesForm.value.forEach(sourceType => {
        this.selectedSources.push(sourceType.value);
      });
    }
    this.refreshEventsViewer();
  }

  onSeverityChange(event) {
    this.selectedSeverity = null;
    if (this.severityForm.value !== undefined && this.severityForm.value.value > 0) {
        this.selectedSeverity = this.severityForm.value;
    }
    this.refreshEventsViewer();
  }

  onEventTypeChange(event) {
    this.selectedEventTypes = [];
    if (this.eventTypeForm.value !== undefined && this.eventTypeForm.value.length > 0) {
      this.eventTypeForm.value.forEach(s => {
        this.selectedEventTypes.push(s);
      });
    }
    this.refreshEventsViewer();
  }

  onScrollHandler(event) {
    const tableViewHeight = event.target.offsetHeight; // viewport: ~500px
    const tableScrollHeight = event.target.scrollHeight; // length of all table
    const scrollLocation = event.target.scrollTop; // how far user scrolled

    // If the user has scrolled within 200px of the bottom, add more data
    const buffer = 600;
    const limit = tableScrollHeight - tableViewHeight - buffer;
    if (scrollLocation > limit && this.searchingEvents === false && this.queryPageStart <= this.totalResults) {
      this.queryPageStart += this.queryPageSize;
      this.searchEvents();
    }
  }

  sortColumn(event) {
    const sort = {
      'sortAttr': event.active,
      'sortOrder': event.direction
    };

    this.sortAttrs = this.sortAttrs.filter((value) => value['sortAttr'] !== sort['sortAttr']);
    this.sortAttrs.push(sort);

    this.refreshEventsViewer();
  }

  updateDisplayedColumns(columns: string[]) {
    this.columnsToDisplay = columns;
  }

  exportCSV() {
    const query = this.buildQuery();
    query.pageSize = 0;
    query.columnsToDisplay = this.columnsToDisplay;
    query.translations = this.severities;
    this.d3eService.get_threats_csv_token(query)
      .pipe(
          catchError(() => of({} as IThreatCsvToken)),
          untilDestroyed(this))
      .subscribe(response => {
        const url = this.configurationService.apiUrl + '/api/events/csv?q=' + response.token;
        multiDownload([url], {
          // tslint:disable-next-line:no-shadowed-variable
          rename: (url, index, urls) => 'event-history.csv',
        });
      });
  }

  exportJSON() {
    const query = this.buildQuery();
    query.pageSize = 0;
    query.columnsToDisplay = this.columnsToDisplay;
    query.translations = this.severities;
    this.d3eService.get_threats_csv_token(query)
      .pipe(
          catchError(() => of({} as IThreatCsvToken)),
          untilDestroyed(this))
      .subscribe(response => {
        const url = this.configurationService.apiUrl + '/api/events/json?q=' + response.token;
        multiDownload([url], {
          // tslint:disable-next-line:no-shadowed-variable
          rename: (url, index, urls) => 'event-history.json',
        });
      });
  }
}
